import React, { useEffect, useState } from 'react';
import ModalePlanning from './ModalePlanning';
import { planningAPI } from '../services/api';
import { useEcranAide } from './aide/AideContexte';

/**
 * « Préparer AAAA-AAAA à partir de AAAA-AAAA » — lot PA.2 (maquette « préparer l'année »,
 * validée par Bertrand le 28/10). Ouvert depuis la vignette d'une année VIDE.
 *
 * Trois écrans : 1 quoi reprendre · 2 de combien décaler (semaines entières) ·
 * 3 l'aperçu (calculé par le serveur, le même calcul que l'écriture) puis
 * « Préparer », tout ou rien. Rien n'est écrit avant le dernier bouton.
 * Les dates qui sortent de l'année ne sont PAS reprises (décidé le 28/10).
 */

const msg = (err, repli) => err?.response?.data?.error || err?.message || repli;

/** « 2026-09-07 » → « lun. 07/09/2026 », lu en UTC : aucun fuseau ne décale le jour. */
export const jourFr = (iso) => (iso
    ? new Date(iso + 'T00:00:00Z').toLocaleDateString('fr-FR', {
        weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC',
    })
    : '');
const plageFr = (p) => jourFr(p.debut) + (p.fin && p.fin !== p.debut ? ' → ' + jourFr(p.fin) : '');
const ajouter = (iso, jours) => {
    const [a, m, j] = iso.split('-').map(Number);
    return new Date(Date.UTC(a, m - 1, j + jours)).toISOString().slice(0, 10);
};
const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;

function Case({ coche, desactive, onChange, titre, detail, nombre, sous }) {
    return (
        <label className={'pl-prep-case' + (sous ? ' sous' : '') + (desactive ? ' off' : '')}>
            <input type="checkbox" checked={coche && !desactive} disabled={desactive} onChange={(e) => onChange(e.target.checked)} />
            <span><b>{titre}</b><span className="pl-hint">{detail}</span></span>
            <span className="n">{nombre}</span>
        </label>
    );
}

function PlanningPreparer({ departementId, anneeId, onFermer, onPrepare }) {
    useEcranAide('accueil/preparer');   // lot PA.4 : l'aide de cette fenêtre, tant qu'elle est ouverte
    const [etat, setEtat] = useState(null);
    const [ecran, setEcran] = useState(1);
    const [options, setOptions] = useState({ evenements: true, dates: true, periodes: true, modeles: true });
    const [semaines, setSemaines] = useState(null);
    const [apercu, setApercu] = useState(null);
    const [erreur, setErreur] = useState('');
    const [enCours, setEnCours] = useState(false);

    useEffect(() => {
        let vivant = true;
        planningAPI.getPreparation(departementId, anneeId)
            .then(({ data }) => {
                if (!vivant) return;
                setEtat(data);
                setSemaines(data.semainesParDefaut);
            })
            .catch((err) => vivant && setErreur('La préparation n\'a pas pu être lue : ' + msg(err, 'erreur inconnue')));
        return () => { vivant = false; };
    }, [departementId, anneeId]);

    // L'aperçu est recalculé par le serveur à l'entrée de l'écran 3 : c'est le même
    // calcul que l'écriture, ce que l'on voit est ce qui s'écrira.
    useEffect(() => {
        if (ecran !== 3) return undefined;
        let vivant = true;
        setApercu(null);
        planningAPI.apercuPreparation(departementId, anneeId, options, semaines)
            .then(({ data }) => vivant && setApercu(data))
            .catch((err) => vivant && setErreur('L\'aperçu n\'a pas pu être calculé : ' + msg(err, 'erreur inconnue')));
        return () => { vivant = false; };
    }, [ecran, departementId, anneeId, options, semaines]);

    const preparer = async () => {
        setEnCours(true); setErreur('');
        try {
            const { data } = await planningAPI.preparer(departementId, anneeId, options, semaines);
            onPrepare(data);
        } catch (err) {
            setErreur(msg(err, 'La préparation a échoué') + ' — rien n\'a été écrit.');
            setEnCours(false);
        }
    };

    const source = etat?.source;
    const cible = etat?.cible;
    const titre = cible && source ? `Préparer ${cible.libelle} à partir de ${source.libelle}` : 'Préparer l\'année';
    const bouger = (n) => setSemaines((s) => Math.min(520, Math.max(1, s + n)));
    const choisirRentree = (iso) => {
        if (!iso || !etat?.rentree) return;
        const jours = Math.round((Date.parse(iso + 'T00:00:00Z') - Date.parse(etat.rentree + 'T00:00:00Z')) / 86400000);
        setSemaines(Math.min(520, Math.max(1, Math.round(jours / 7))));
    };
    const majOption = (cle, v) => setOptions((o) => ({ ...o, [cle]: v, ...(cle === 'evenements' && !v ? { dates: false } : {}),
        ...(cle === 'evenements' && v ? { dates: true } : {}) }));
    const rien = !options.evenements && !options.periodes && !options.modeles;

    const noms = ['Quoi reprendre', 'Décaler les dates', 'Vérifier et préparer'];
    const nouvelleRentree = etat?.rentree && semaines ? ajouter(etat.rentree, semaines * 7) : null;

    return (
        <ModalePlanning
            titre={titre}
            icone="fa-copy"
            taille="lg"
            onFermer={enCours ? () => {} : onFermer}
            pied={
                <>
                    <span className="pl-hint" style={{ marginRight: 'auto' }}>
                        {ecran < 3 ? `Étape ${ecran} sur 3 — rien n'est encore écrit`
                            : <><i className="fa-solid fa-circle-info" /> Le journal gardera qui a préparé l'année, et depuis quelle année</>}
                    </span>
                    <button className="pl-btn" onClick={onFermer} disabled={enCours}>Annuler</button>
                    {ecran > 1 && (
                        <button className="pl-btn" onClick={() => setEcran(ecran - 1)} disabled={enCours}>
                            <i className="fa-solid fa-arrow-left" /> Précédent
                        </button>
                    )}
                    {ecran < 3 ? (
                        <button className="pl-btn pl-btn-primary" onClick={() => setEcran(ecran + 1)} disabled={!etat?.source || rien}
                            title={rien ? 'Cochez au moins une chose à reprendre' : undefined}>
                            Suivant <i className="fa-solid fa-arrow-right" />
                        </button>
                    ) : (
                        <button className="pl-btn pl-btn-primary" onClick={preparer} disabled={!apercu || enCours}>
                            {enCours ? <><i className="fa-solid fa-spinner fa-spin" /> Préparation…</>
                                : <><i className="fa-solid fa-copy" /> Préparer {cible?.libelle}</>}
                        </button>
                    )}
                </>
            }
        >
            <div className="pl-prep-pas">
                {noms.map((n, i) => (
                    <span key={n} className={i + 1 === ecran ? 'on' : (i + 1 < ecran ? 'fait' : '')}>
                        <b>{i + 1 < ecran ? <i className="fa-solid fa-check" /> : i + 1}</b> {n}
                    </span>
                ))}
            </div>

            {erreur && <div className="pl-bandeau pl-bandeau-warn"><i className="fa-solid fa-triangle-exclamation" /> {erreur}</div>}
            {!etat && !erreur && <div className="pl-hint"><i className="fa-solid fa-spinner fa-spin" /> Lecture de l'année précédente…</div>}
            {etat && !etat.source && (
                <div className="pl-bandeau pl-bandeau-info">
                    <i className="fa-solid fa-circle-info" /> Aucune année précédente n'a de planning à reprendre : partez de zéro.
                </div>
            )}

            {etat?.source && ecran === 1 && (
                <>
                    <div className="pl-hint" style={{ marginBottom: 8 }}>
                        Ce qui appartient à <b>{source.libelle}</b> et se recopie dans {cible.libelle}. Rien n'est écrit avant le dernier écran.
                    </div>
                    <Case coche={options.evenements} onChange={(v) => majOption('evenements', v)}
                        titre="Les événements de l'année" nombre={pluriel(etat.compte.evenements, 'événement', 'événements')}
                        detail="leurs libellés, catégories et couleurs — comme « Récupérer les événements N-1 » du calendrier" />
                    <Case sous coche={options.dates} desactive={!options.evenements} onChange={(v) => majOption('dates', v)}
                        titre="leurs dates" nombre={pluriel(etat.compte.dates, 'date', 'dates')}
                        detail="décalées à l'écran suivant ; chaque date garde son groupe" />
                    <Case coche={options.periodes} onChange={(v) => majOption('periodes', v)}
                        titre="Les périodes de chaque groupe"
                        nombre={pluriel(etat.compte.periodes, 'période', 'périodes') + ' · ' + pluriel(etat.compte.groupes, 'groupe', 'groupes')}
                        detail="Formation / Entreprise / Stage, décalées à l'écran suivant" />
                    <Case coche={options.modeles} onChange={(v) => majOption('modeles', v)}
                        titre="Les modèles d'export"
                        nombre={pluriel(etat.compte.modeles, 'modèle', 'modèles') + ' · ' + pluriel(etat.compte.vignettes, 'vignette', 'vignettes')}
                        detail={<>leurs réglages, leur case « sur l'accueil » ; « {source.libelle} » devient « {cible.libelle} » dans leurs titres
                            {!options.evenements && <> — <b>sans les événements, leurs choix d'événements sont perdus</b></>}</>} />
                    <div className="pl-prep-commun">
                        <i className="fa-solid fa-circle-info" /> Les types de journées, les catégories d'événements et les formations
                        (niveaux, groupes, heures) sont <b>communs à toutes les années</b> : ils sont déjà là. Les <b>versions</b> de{' '}
                        {source.libelle} restent à {source.libelle} : elles sont l'historique de cette année-là.
                    </div>
                </>
            )}

            {etat?.source && ecran === 2 && (
                <>
                    <div className="pl-hint" style={{ marginBottom: 8 }}>
                        Toutes les dates reprises avancent d'un <b>nombre entier de semaines</b> : un lundi reste un lundi, une période
                        du lundi au vendredi reste du lundi au vendredi.
                    </div>
                    <div className="pl-prep-decal">
                        <div className="bloc">
                            <div className="lib">Rentrée {source.libelle} (première date posée)</div>
                            <div className="val">{jourFr(etat.rentree)}</div>
                        </div>
                        <div className="fleche">+ {semaines} semaines<br /><span className="pl-hint">{semaines * 7} jours</span></div>
                        <div className="bloc">
                            <div className="lib">Rentrée {cible.libelle}</div>
                            <div className="val">{jourFr(nouvelleRentree)}</div>
                            <div className="gestes">
                                <button className="pl-btn" onClick={() => bouger(-1)}>− 1 semaine</button>
                                <input type="date" className="pl-f" value={nouvelleRentree || ''} onChange={(e) => choisirRentree(e.target.value)} />
                                <button className="pl-btn" onClick={() => bouger(1)}>+ 1 semaine</button>
                            </div>
                        </div>
                    </div>
                    {nouvelleRentree && cible.debut && nouvelleRentree < cible.debut && (
                        <div className="pl-bandeau pl-bandeau-warn">
                            <i className="fa-solid fa-triangle-exclamation" /> La rentrée tomberait avant le début de l'année ({jourFr(cible.debut)}) :
                            les dates d'avant ne seront pas reprises.
                        </div>
                    )}
                    <div className="pl-hint" style={{ marginBottom: 6 }}>
                        Par défaut : 52 semaines par année d'écart, une de plus si la rentrée tombait avant le début de l'année
                        ({semaines === etat.semainesParDefaut ? 'c\'est la valeur affichée' : `ici ${etat.semainesParDefaut}`}).
                        Une autre date de rentrée ? Choisissez-la : le décalage se recalcule, arrondi à la semaine.
                    </div>
                    <table className="pl-prep-table">
                        <thead><tr><th>Exemples</th><th>en {source.libelle}</th><th>en {cible.libelle}</th></tr></thead>
                        <tbody>
                            {etat.exemples.map((x, i) => (
                                <tr key={i}>
                                    <td>{x.libelle}</td>
                                    <td>{plageFr(x)}</td>
                                    <td><b>{plageFr({ debut: ajouter(x.debut, semaines * 7), fin: x.fin ? ajouter(x.fin, semaines * 7) : null })}</b></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {etat?.source && ecran === 3 && (
                !apercu ? (!erreur && <div className="pl-hint"><i className="fa-solid fa-spinner fa-spin" /> Calcul de l'aperçu…</div>) : (
                    <>
                        <div className="pl-prep-resume">
                            <div><b>{apercu.compte.evenements}</b><span>événements</span></div>
                            <div><b>{apercu.compte.dates}</b><span>dates</span></div>
                            <div><b>{apercu.compte.periodes}</b><span>périodes</span></div>
                            <div><b>{apercu.compte.modeles}</b><span>modèles d'export</span></div>
                        </div>
                        <div className="pl-hint" style={{ marginBottom: 6 }}>
                            Décalage : <b>+{apercu.semaines} semaines</b>.{' '}
                            {apercu.aVerifier.length
                                ? (apercu.aVerifier.length > 1
                                    ? <><b className="pl-prep-alerte">{apercu.aVerifier.length} dates tombent mal</b> : reprises quand même, à leur
                                        place décalée ; le calendrier les signale jusqu'à ce que vous les ayez vues.</>
                                    : <><b className="pl-prep-alerte">1 date tombe mal</b> : reprise quand même, à sa place décalée ; le
                                        calendrier la signale jusqu'à ce que vous l'ayez vue.</>)
                                : 'Aucune date reprise ne tombe un jour férié ou pendant des vacances.'}
                        </div>
                        {apercu.aVerifier.length > 0 && (
                            <table className="pl-prep-table">
                                <thead><tr><th>À vérifier</th><th>en {source.libelle}</th><th>en {cible.libelle}</th><th>pourquoi</th></tr></thead>
                                <tbody>
                                    {apercu.aVerifier.map((x, i) => (
                                        <tr key={i}>
                                            <td>{x.quoi === 'periode' ? 'Période' : 'Événement'} · {x.libelle}</td>
                                            <td>{plageFr(x.avant)}</td>
                                            <td><b>{plageFr(x.apres)}</b></td>
                                            <td className="motif">{x.motif}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {apercu.nonReprises.length > 0 && (
                            <>
                                <div className="pl-hint" style={{ margin: '10px 0 6px' }}>
                                    <b className="pl-prep-alerte">{pluriel(apercu.nonReprises.length, 'date ne sera pas reprise', 'dates ne seront pas reprises')}</b> :
                                    décalées, elles sortent de l'année {cible.libelle} ({jourFr(cible.debut)} → {jourFr(cible.fin)}) et y seraient invisibles.
                                </div>
                                <table className="pl-prep-table">
                                    <thead><tr><th>Non reprise</th><th>en {source.libelle}</th><th>serait en {cible.libelle}</th></tr></thead>
                                    <tbody>
                                        {apercu.nonReprises.map((x, i) => (
                                            <tr key={i}>
                                                <td>{x.quoi === 'periode' ? 'Période' : 'Événement'} · {x.libelle}</td>
                                                <td>{plageFr(x.avant)}</td>
                                                <td>{plageFr(x.apres)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                        <div className="pl-prep-commun" style={{ marginTop: 10 }}>
                            <i className="fa-solid fa-shield-halved" /> <b>Tout ou rien</b> : la préparation s'écrit d'un seul bloc ; si quelque
                            chose échoue, rien n'est écrit et {cible.libelle} reste vide. Elle n'est possible que sur une année <b>vide</b> :
                            elle n'écrase jamais rien.
                        </div>
                    </>
                )
            )}
        </ModalePlanning>
    );
}

export default PlanningPreparer;
